/**
 * Helper functions to add or remove highlights to a text.
 * Specifically used for push-words and pull-words to mark them,
 * so that users can see words they should keep or replace.
 */

/**
 * Creates the markup for a single highlight.
 * @param {Object} highlight the data for the highlight
 * @returns {string} the HTML for the highlight as a string
 */
function createHighlight(highlight) {
    const isPull =
        highlight.advice.class.name === 'pull' ||
        highlight.advice.class.name === 'pull-bold';
    const styleClass = isPull ? 'ppw-pull' : 'ppw-push';

    const highlightEl = document.createElement('span');
    highlightEl.classList.add('ppw-highlight', styleClass);
    highlightEl.dataset.tooltip = highlight.advice.adviceText;
    highlightEl.innerText = highlight.advice.phrase;

    const highlightHTML = highlightEl.outerHTML;
    highlightEl.remove();
    return highlightHTML;
}

/**
 * Removes all highlights from a text.
 * @param {string} text the text containing highlights
 * @return {string} the sanitized text
 */
export function removeHighlights(text) {
    const dom = document.createElement('html');
    dom.innerHTML = '<head></head><body></body>';
    const body = dom.querySelector('body');
    body.innerHTML = text;

    const tooltips = body.querySelectorAll('.ppw-tooltip');
    tooltips.forEach((tooltip) => tooltip.remove());

    const highlights = body.querySelectorAll('.ppw-highlight');
    highlights.forEach((highlight) => {
        const content = highlight.innerHTML;
        highlight.insertAdjacentHTML('afterend', content);
        highlight.remove();
    });

    const sanitizedText = body.innerHTML;
    dom.remove();
    return sanitizedText;
}

/**
 * Wraps the highlights in the text in <span>-Elements, so that they can be displayed
 * as pull-words or push-words.
 * @param {string} text the text that the highlights should be added to
 * @param {Object} highlights the highlight data for the given text
 * @returns {string} the text including its HTML-highlights
 */
export function addHighlights(text, highlights) {
    let highlightedText = '';
    let charPosition = 0;

    for (const highlight of highlights) {
        // copy original text from the end of the last highlight up to the next highlight
        // then add the highlight tag and continue
        const position = highlight.position;
        highlightedText += text.substring(charPosition, position.start);
        highlightedText += createHighlight(highlight);
        charPosition = position.end;
    }
    highlightedText += text.substring(charPosition);
    return highlightedText;
}

/**
 * Registers mouseenter and mouseout handlers on an activator
 * that show/hide a tooltip for the target.
 * @param activator
 */
export function registerTooltip(activator) {
    if (activator.dataset.handler === 'true') {
        return;
    }

    activator.dataset.handler = 'true';

    activator.addEventListener('mouseenter', () => {
        if (activator.querySelector('.ppw-tooltip')) {
            return;
        }

        const top = activator.offsetTop >= 300;
        const parentWidth = activator.offsetParent.clientWidth;
        const activatorWidth = activator.offsetWidth;
        const offsetLeft =
            activator.offsetLeft + activatorWidth > parentWidth - 300;

        const classList = ['ppw-tooltip'];
        if (!top) {
            classList.push('tooltip-bottom');
        }
        if (offsetLeft) {
            classList.push('tooltip-offset-left');
        }

        const id = 'ppw_' + generateID();
        const tooltip = activator.ownerDocument.createElement('div');
        tooltip.classList.add(...classList);
        tooltip.id = id;
        tooltip.contentEditable = 'false';
        tooltip.append(activator.dataset.tooltip);

        activator.append(tooltip);
        activator.dataset.tooltipElement = id;
    });

    activator.addEventListener('mouseout', () => {
        const id = activator.dataset.tooltipElement;
        const tooltip = activator.querySelector(`#${id}`);
        if (tooltip) {
            tooltip.remove();
        }
    });
}

/**
 * Generate a (pseudo) random string that can be used as a unique identifier
 * @returns {string}
 */
function generateID() {
    if (crypto?.randomUUID) {
        return crypto.randomUUID();
    }
    return Math.floor(Math.random() * 10000) + '';
}
